import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import Input from '../../components/UI/Input';
import { ArrowLeft, Calendar, MapPin, Users } from 'lucide-react';
import { trainingAPI, membersAPI } from '../../services/api';
import { toast } from 'react-toastify';

const RegistrationContainer = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background.primary};
  padding: 2rem 0;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const BackButton = styled(Button)`
  margin-bottom: 2rem;
`;

const EventCard = styled(Card)`
  padding: 2rem;
  margin-bottom: 2rem;
`;

const EventHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const EventIcon = styled.div`
  width: 60px;
  height: 60px;
  background-color: ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.white};
`;

const EventInfo = styled.div`
  flex: 1;
`;

const EventTitle = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0 0 0.5rem 0;
`;

const EventMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const EventDescription = styled.p`
  color: ${props => props.theme.colors.text.primary};
  line-height: 1.6;
  margin: 0;
`;

const RegistrationForm = styled(Card)`
  padding: 2rem;
`;

const FormTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0 0 1rem 0;
`;

const FormDescription = styled.p`
  color: ${props => props.theme.colors.text.secondary};
  margin: 0 0 2rem 0;
`;

const FormFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FieldLabel = styled.label`
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const FieldInput = styled.input`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
  
  &:invalid {
    border-color: ${props => props.theme.colors.error};
  }
`;

const FieldTextarea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const FieldSelect = styled.select`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CheckboxOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const FileInput = styled.input`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  font-size: ${props => props.theme.typography.fontSize.sm};
  margin-top: 0.25rem;
`;

const SuccessMessage = styled.div`
  background-color: ${props => props.theme.colors.success}20;
  border: 1px solid ${props => props.theme.colors.success};
  color: ${props => props.theme.colors.success};
  padding: 1rem;
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: 1rem;
`;

const MembershipLookup = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border.light};
  background-color: ${props => props.theme.colors.background.secondary};
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MembershipLookupHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  strong {
    font-size: ${props => props.theme.typography.fontSize.base};
    color: ${props => props.theme.colors.text.primary};
  }

  span {
    font-size: ${props => props.theme.typography.fontSize.sm};
    color: ${props => props.theme.colors.text.secondary};
  }
`;

const MembershipLookupRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: flex-end;
`;

const MembershipLookupField = styled.div`
  flex: 1;
  min-width: 220px;
`;

const MembershipLookupActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const AutoFillHint = styled.span`
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.text.secondary};
`;

const MEMBERSHIP_FIELD_MAP = {
  full_name: 'full_name',
  name: 'full_name',
  applicant_name: 'full_name',
  email: 'email',
  email_address: 'email',
  phone: 'phone',
  phone_number: 'phone',
  organization: 'organization',
  organization_name: 'organization',
  role_title: 'role_title',
  position: 'role_title',
  occupation: 'role_title',
};

const TrainingRegistration = () => {
  const { trainingId } = useParams();
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [loadingTraining, setLoadingTraining] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [membershipId, setMembershipId] = useState('');
  const [membershipLookupLoading, setMembershipLookupLoading] = useState(false);
  const [membershipError, setMembershipError] = useState('');
  const [linkedMember, setLinkedMember] = useState(null);
  const [autoFilledKeys, setAutoFilledKeys] = useState([]);

  useEffect(() => {
    const fetchTraining = async () => {
      setLoadingTraining(true);
      setLoadError(null);
      try {
        const response = await trainingAPI.getById(trainingId);
        setTraining(response.data);
      } catch (error) {
        console.error('Error loading training:', error);
        setLoadError('The requested training could not be found or is no longer available.');
      } finally {
        setLoadingTraining(false);
      }
    };

    fetchTraining();
  }, [trainingId]);

  const registrationForm = useMemo(() => {
    if (!training) return null;
    return training.registration_form || training.registrationForm || null;
  }, [training]);

  const reservedSeats = useMemo(() => {
    if (!training) return 0;
    if (typeof training.reserved_seats === 'number') {
      return training.reserved_seats;
    }
    const pending = training.registration_counts_pending || 0;
    const approved = training.registration_counts_approved || 0;
    return pending + approved;
  }, [training]);

  const remainingSeats = useMemo(() => {
    if (!training?.max_participants) return null;
    if (typeof training.remaining_seats === 'number') {
      return Math.max(training.remaining_seats, 0);
    }
    return Math.max(training.max_participants - reservedSeats, 0);
  }, [training, reservedSeats]);

  const isFull = useMemo(() => {
    if (!training?.max_participants) return false;
    if (typeof training.is_full === 'boolean') {
      return training.is_full;
    }
    return reservedSeats >= training.max_participants;
  }, [training, reservedSeats]);

  const getFieldKey = (field) => field?.key || field?.id?.toString();

  const getFieldValue = (keys) => {
    for (const key of keys) {
      if (key && formData[key] !== undefined) {
        const value = formData[key];
        if (Array.isArray(value)) {
          return value.join(', ');
        }
        return value;
      }
    }
    return '';
  };

  const handleInputChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
    
    // Clear error when user starts typing
    if (errors[fieldId]) {
      setErrors(prev => ({
        ...prev,
        [fieldId]: ''
      }));
    }
  };

  const runMemberAutoFill = useCallback((member) => {
    if (!registrationForm || !member) return;

    const source = {
      full_name: member.full_name || member.first_name || '',
      email: member.email,
      phone: member.phone,
      organization: member.organization,
      role_title: member.role_title,
      membership_id: member.member_id,
    };

    const updates = {};
    const locked = [];

    registrationForm.fields.forEach((field) => {
      const key = getFieldKey(field);
      if (!key) return;

      if (key === 'membership_id' && source.membership_id) {
        updates[key] = source.membership_id;
        locked.push(key);
        return;
      }

      const mappedKey = MEMBERSHIP_FIELD_MAP[key];
      if (mappedKey && source[mappedKey]) {
        updates[key] = source[mappedKey];
        locked.push(key);
      }
    });

    if (Object.keys(updates).length) {
      setFormData((prev) => ({
        ...prev,
        ...updates,
      }));
    }
    setAutoFilledKeys(locked);
  }, [registrationForm]);

  useEffect(() => {
    if (linkedMember) {
      runMemberAutoFill(linkedMember);
    }
  }, [linkedMember, runMemberAutoFill]);

  const validateForm = () => {
    if (!registrationForm) return false;
    const newErrors = {};
    
    registrationForm.fields.forEach(field => {
      const key = getFieldKey(field);
      const value = formData[key];

      if (field.required) {
        if (field.type === 'checkbox' && Array.isArray(value)) {
          if (!value.length) {
            newErrors[key] = `${field.label} is required`;
          }
        } else if (!value || value.toString().trim() === '') {
          newErrors[key] = `${field.label} is required`;
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleMembershipLookup = async () => {
    if (!membershipId.trim()) {
      setMembershipError('Please enter a membership ID to continue.');
      return;
    }

    setMembershipLookupLoading(true);
    setMembershipError('');
    try {
      const response = await membersAPI.lookupByMembershipId(membershipId.trim());
      setLinkedMember(response.data);
      setMembershipId(response.data.member_id);
      toast.success('Member profile loaded successfully.');
    } catch (error) {
      setLinkedMember(null);
      setAutoFilledKeys([]);
      const message = error.response?.data?.message || 'Unable to find a member with that ID.';
      setMembershipError(message);
      toast.error(message);
    } finally {
      setMembershipLookupLoading(false);
    }
  };

  const handleClearMembership = () => {
    setMembershipId('');
    setLinkedMember(null);
    setAutoFilledKeys([]);
    setMembershipError('');
  };

  const handleMembershipInputChange = (value) => {
    setMembershipId(value);
    setMembershipError('');
    if (linkedMember) {
      setLinkedMember(null);
      setAutoFilledKeys([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!registrationForm || !training) return;
    if (!validateForm()) return;
    if (membershipId && !linkedMember) {
      setMembershipError('Please fetch your membership details before submitting.');
      toast.error('Please fetch your membership details before submitting.');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        full_name: getFieldValue(['full_name', 'name', 'applicant_name']),
        email: getFieldValue(['email', 'email_address']),
        phone: getFieldValue(['phone', 'phone_number']),
        organization: getFieldValue(['organization', 'organization_name']),
        role_title: getFieldValue(['role_title', 'position', 'occupation']),
        region: getFieldValue(['region']),
        motivation: getFieldValue(['motivation', 'motivation_for_attending', 'motivation_for_training']),
        special_needs: getFieldValue(['special_needs', 'accessibility']),
        form_data: formData,
      };

      if (linkedMember?.member_id) {
        payload.membership_id = linkedMember.member_id;
      } else if (membershipId) {
        payload.membership_id = membershipId;
      }

      await trainingAPI.register(training.id, payload);
      setIsSubmitted(true);
      toast.success('Your registration has been submitted.');
    } catch (error) {
      console.error('Failed to submit registration:', error);
      const message = error.response?.data?.message || 'Unable to submit registration. Please try again.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const key = getFieldKey(field);
    const value = formData[key] || (field.type === 'checkbox' ? [] : '');
    const error = errors[key];
    const isAutoFilledField = linkedMember && autoFilledKeys.includes(key);

    switch (field.type) {
      case 'textarea':
        return (
          <>
          <FieldTextarea
            value={value}
              onChange={(e) => handleInputChange(key, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
              readOnly={isAutoFilledField}
          />
            {isAutoFilledField && <AutoFillHint>Synced from your membership profile.</AutoFillHint>}
          </>
        );
      
      case 'select':
        return (
          <>
          <FieldSelect
            value={value}
              onChange={(e) => handleInputChange(key, e.target.value)}
            required={field.required}
              disabled={isAutoFilledField}
          >
            <option value="">{field.placeholder}</option>
            {field.options?.map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </FieldSelect>
            {isAutoFilledField && <AutoFillHint>Synced from your membership profile.</AutoFillHint>}
          </>
        );
      
      case 'radio':
        return (
          <>
          <RadioGroup>
            {field.options?.map((option, index) => (
              <RadioOption key={index}>
                <input
                  type="radio"
                    name={`field_${key}`}
                  value={option}
                  checked={value === option}
                    onChange={(e) => handleInputChange(key, e.target.value)}
                  required={field.required}
                    disabled={isAutoFilledField}
                />
                {option}
              </RadioOption>
            ))}
          </RadioGroup>
            {isAutoFilledField && <AutoFillHint>Synced from your membership profile.</AutoFillHint>}
          </>
        );
      
      case 'checkbox':
        const checkboxValues = value || [];
        return (
          <>
          <CheckboxGroup>
            {field.options?.map((option, index) => (
              <CheckboxOption key={index}>
                <input
                  type="checkbox"
                  value={option}
                  checked={checkboxValues.includes(option)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...checkboxValues, option]
                      : checkboxValues.filter(v => v !== option);
                      handleInputChange(key, newValues);
                  }}
                    disabled={isAutoFilledField}
                />
                {option}
              </CheckboxOption>
            ))}
          </CheckboxGroup>
            {isAutoFilledField && <AutoFillHint>Synced from your membership profile.</AutoFillHint>}
          </>
        );
      
      case 'file':
        return (
          <FileInput
            type="file"
            onChange={(e) => handleInputChange(field.id, e.target.files[0])}
            required={field.required}
          />
        );
      
      default:
        return (
          <>
          <FieldInput
              type={
                field.type === 'email'
                  ? 'email'
                  : field.type === 'phone'
                  ? 'tel'
                  : field.type === 'number'
                  ? 'number'
                  : 'text'
              }
            value={value}
              onChange={(e) => handleInputChange(key, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
              readOnly={isAutoFilledField}
              disabled={isAutoFilledField}
          />
            {isAutoFilledField && <AutoFillHint>Synced from your membership profile.</AutoFillHint>}
          </>
        );
    }
  };

  if (loadingTraining) {
    return (
      <RegistrationContainer>
        <Container>
          <Card padding="3rem" textAlign="center">
            <h1>Loading training…</h1>
          </Card>
        </Container>
      </RegistrationContainer>
    );
  }

  if (loadError || !training) {
    return (
      <RegistrationContainer>
        <Container>
          <Card padding="3rem" textAlign="center">
            <h1>Training Not Found</h1>
            <p>{loadError || 'The requested training could not be found.'}</p>
            <Button variant="primary" onClick={() => navigate('/')}>
              Return Home
            </Button>
          </Card>
        </Container>
      </RegistrationContainer>
    );
  }

  if (!registrationForm) {
    return (
      <RegistrationContainer>
        <Container>
          <Card padding="3rem" textAlign="center">
            <h1>Registration Unavailable</h1>
            <p>The registration form for this training is not configured yet.</p>
          </Card>
        </Container>
      </RegistrationContainer>
    );
  }

  if (isFull && !isSubmitted) {
    return (
      <RegistrationContainer>
        <Container>
          <BackButton 
            variant="outline" 
            leftIcon={<ArrowLeft size={16} />}
            onClick={() => navigate('/')}
          >
            Back to Home
          </BackButton>

          <Card padding="3rem" textAlign="center">
            <h1>Registration Closed</h1>
            <p style={{ marginBottom: '1rem' }}>
              {training.max_participants
                ? `All ${training.max_participants} seats for "${training.title}" have already been reserved.`
                : 'This training is no longer accepting registrations.'}
            </p>
            <p style={{ color: '#6b7280' }}>
              Please check back later or explore other upcoming trainings on our events page.
            </p>
            <Button variant="primary" style={{ marginTop: '1.5rem' }} onClick={() => navigate('/')}>
              Browse Other Programs
            </Button>
          </Card>
        </Container>
      </RegistrationContainer>
    );
  }

  if (isSubmitted) {
    return (
      <RegistrationContainer>
        <Container>
          <BackButton 
            variant="outline" 
            leftIcon={<ArrowLeft size={16} />}
            onClick={() => navigate('/')}
          >
            Back to Home
          </BackButton>
          
          <Card padding="3rem" textAlign="center">
            <SuccessMessage>
              <h2>Registration Submitted!</h2>
              <p>Thank you for registering for "{training.title}". We will follow up with next steps.</p>
            </SuccessMessage>
            
            <Button variant="primary" onClick={() => navigate('/')}>
              Return Home
            </Button>
          </Card>
        </Container>
      </RegistrationContainer>
    );
  }

  const startDate = training.start_date
    ? new Date(training.start_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'TBD';
  const endDate = training.end_date
    ? new Date(training.end_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  return (
    <RegistrationContainer>
      <Container>
        <BackButton 
          variant="outline" 
          leftIcon={<ArrowLeft size={16} />}
          onClick={() => navigate(-1)}
        >
          Back to Trainings
        </BackButton>

        <EventCard>
          <EventHeader>
            <EventIcon>
              <Calendar size={24} />
            </EventIcon>
            <EventInfo>
              <EventTitle>{training.title}</EventTitle>
              <EventMeta>
                <MetaItem>
                  <Calendar size={16} />
                  {startDate}{endDate ? ` - ${endDate}` : ''}
                </MetaItem>
                {training.location && (
                <MetaItem>
                  <MapPin size={16} />
                  {training.location}
                </MetaItem>
                )}
                {training.max_participants && (
                <MetaItem>
                  <Users size={16} />
                    {remainingSeats !== null
                      ? `${remainingSeats} of ${training.max_participants} seats left`
                      : `Max ${training.max_participants} participants`}
                </MetaItem>
                )}
              </EventMeta>
            </EventInfo>
          </EventHeader>
          <EventDescription>{training.description}</EventDescription>
        </EventCard>

        <RegistrationForm>
          <FormTitle>{registrationForm.title}</FormTitle>
          <FormDescription>{registrationForm.description}</FormDescription>

          <MembershipLookup>
            <MembershipLookupHeader>
              <strong>Already a member?</strong>
              <span>Enter your membership ID to auto-fill your profile details and link this training to your member record.</span>
            </MembershipLookupHeader>
            <MembershipLookupRow>
              <MembershipLookupField>
                <FieldLabel>
                  Membership ID <span style={{ color: '#6b7280' }}>(optional)</span>
                </FieldLabel>
                <FieldInput
                  value={membershipId}
                  onChange={(e) => handleMembershipInputChange(e.target.value)}
                  placeholder="e.g., MEM-00123"
                />
                {linkedMember && (
                  <AutoFillHint>Synced with {linkedMember.full_name} ({linkedMember.member_id}).</AutoFillHint>
                )}
              </MembershipLookupField>
              <MembershipLookupActions>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleMembershipLookup}
                  disabled={!membershipId || membershipLookupLoading}
                  loading={membershipLookupLoading}
                >
                  {linkedMember ? 'Refresh Details' : 'Fetch Details'}
                </Button>
                {linkedMember && (
                  <Button type="button" variant="ghost" onClick={handleClearMembership}>
                    Clear
                  </Button>
                )}
              </MembershipLookupActions>
            </MembershipLookupRow>
            {membershipError && <ErrorMessage>{membershipError}</ErrorMessage>}
            {linkedMember && !membershipError && (
              <SuccessMessage style={{ marginBottom: 0 }}>
                Member profile linked. Contact fields below are read-only to preserve membership data.
              </SuccessMessage>
            )}
          </MembershipLookup>
          
          <form onSubmit={handleSubmit}>
            <FormFields>
              {registrationForm.fields.map((field) => (
                <FormField key={getFieldKey(field)}>
                  <FieldLabel>
                    {field.label}{field.required && <span style={{ color: 'red' }}> *</span>}
                  </FieldLabel>
                  {renderField(field)}
                  {errors[getFieldKey(field)] && <ErrorMessage>{errors[getFieldKey(field)]}</ErrorMessage>}
                </FormField>
              ))}
            </FormFields>
            
            <Button
              variant="primary"
              type="submit"
              size="large"
              loading={submitting}
              disabled={submitting || isFull}
            >
              {submitting ? 'Submitting...' : 'Submit Registration'}
            </Button>
          </form>
        </RegistrationForm>
      </Container>
    </RegistrationContainer>
  );
};

export default TrainingRegistration;


















